/**
 * /w/:slug/app/calendar — Calendar & trust center (V98-5-6).
 *
 *   header      "All programs · N ahead · M in the next 14 days · K overdue"
 *               (V98-5 review T5-B5: "ahead" counts dates from today on)
 *   Next up     (h2, the only card on the page — T5-H4) the next report to
 *               sign: its state chip is the Report tab's, each prerequisite is
 *               named as the task that clears it with its owner and due date,
 *               and the viewer's own rows carry their action inline; "Open in
 *               <program>" goes to the draft's own program
 *   Calendar    an agenda list (72px date column, dividers, today marked,
 *               rows beyond 14 days dimmed) of every dated obligation, filtered
 *               by program; each row opens the record that moves it (grants →
 *               #grant-<id>, acceptances → /exceptions?acceptance=<key>)
 *   Trust center (owner/admin) who can see what. Approving a request issues
 *               the access in the same call and shows it ONCE ("Copy access
 *               link"; T5-B3); each grant row has Re-issue access, Extend…
 *               (≤ 366 days) and Revoke… (reason). Requests: Approve… (the
 *               filled primary) / Decline…; the access log (7 days). Other
 *               members get the calendar and who to ask.
 *
 * Reads: GET /calendar (member), GET /trust-center (owner/admin).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Field, Input, PageHeader, Select, Spinner, Textarea } from '@aigovops/design-system';
import { programHas, type CalendarEntry, type CdsAccessRequest, type IssuedAccess, type PackagePrerequisite, type PackageView, type TrustCenterGrant } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import { useAuth } from '../lib/auth';
import {
  approveAccessRequest, confirmGrantUsage, declineAccessRequest, extendGrant, getCalendar, getTrustCenter, listPackageDrafts, recordAccessRequest,
  reissueGrantAccess, revokeGrant,
} from '../lib/cert-ops-api';
import { fmtDate, prereqTask } from '../lib/cert-ops-model';
import { PROGRAM_LABELS, SCN_PROGRAM } from '../lib/nav-config';
import { usePrograms } from '../lib/workspace-navigation';
import { nameOf, firstName, type Names } from '../lib/today-model';
import { AskAdmin } from '../components/AskAdmin';
import { ClearDialog, draftState } from '../components/programs/ReportPackages';
import { ActionDialog, Chip, JumpLink, errorText, useAnnounce, useFocusAfter } from '../components/today/shared';
import { useNames } from './Today';
import './today.css';
import './cert-ops.css';

const KIND_LABEL: Record<string, string> = {
  certification_package_overview: 'Certification package overview',
  security_decision_record: 'Security decision record',
  ongoing_certification_report: 'Ongoing certification report',
};
const DAY = 86_400_000;
const MAX_GRANT_DAYS = 366;
const programWord = (k: string | null) => (k ? PROGRAM_LABELS[k as keyof typeof PROGRAM_LABELS] ?? k : 'All programs');
const dayOf = (ms: number) => new Date(ms).toISOString().slice(0, 10);

export function CalendarTrust() {
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const isAdmin = role === 'owner' || role === 'admin';
  const me = useAuth().user?.id ?? '';
  const names = useNames(slug);
  const [announceNode, announce] = useAnnounce();
  const cal = useQuery({ queryKey: ['calendar', slug], queryFn: () => getCalendar(slug), retry: false });
  const tc = useQuery({ queryKey: ['trust-center', slug], queryFn: () => getTrustCenter(slug), retry: false, enabled: isAdmin });
  const programs = usePrograms(slug);
  // The trust center (CDS) program: its package drafts and access grants (FedRAMP).
  const fedramp = programs.data?.programs.find((p) => programHas(p.program, (m) => m.features.cds)) ?? null;
  const drafts = useQuery({ queryKey: ['package-drafts', slug, fedramp?.profile_id ?? ''], queryFn: () => listPackageDrafts(slug, fedramp!.profile_id), enabled: !!fedramp, retry: false });
  const [program, setProgram] = useState<string>('all');

  // A deep link to #grant-<id> (from the calendar or a prerequisite) lands on that grant row, focused (T5-H15).
  useEffect(() => {
    const id = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
    if (!id || !tc.data) return;
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView?.({ block: 'center' }); el.focus(); }
  }, [tc.data]);

  const entries = useMemo(() => (cal.data?.entries ?? []).filter((e) => program === 'all' || e.program === program), [cal.data, program]);
  const months = useMemo(() => {
    const m = new Map<string, CalendarEntry[]>();
    for (const e of entries) {
      const k = new Date(e.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
      m.set(k, [...(m.get(k) ?? []), e]);
    }
    return [...m.entries()];
  }, [entries]);
  const next = drafts.data?.drafts.find((d) => d.lifecycle.state !== 'signed' && d.lifecycle.state !== 'released') ?? null;
  const usedPrograms = [...new Set((cal.data?.entries ?? []).map((e) => e.program).filter((p): p is string => !!p))];
  const c = cal.data?.counts;
  const keyOfProfile = (pid: string) => programs.data?.programs.find((p) => p.profile_id === pid)?.program ?? SCN_PROGRAM ?? '';

  return (
    <div className="gp-item">
      {announceNode}
      <PageHeader variant="app" title="Calendar & trust center" headingId="cal-h1"
        subtitle={c ? `All programs · ${c.ahead} ahead · ${c.next_14_days} in the next 14 days${c.overdue ? ` · ${c.overdue} overdue` : ''}` : undefined} />

      {next ? <NextUp slug={slug} prefix={prefix} v={next} programKey={keyOfProfile(next.draft.profile_id)} me={me} names={names} onDone={announce} /> : null}

      <div className="gp-split">
        <section className="gp-sec" aria-labelledby="cal-h">
          <h2 id="cal-h" className="gp-sec-h">Calendar</h2>
          {usedPrograms.length > 1 ? (
            <ul className="gp-filter" aria-label="Program">
              <li><button type="button" aria-pressed={program === 'all'} onClick={() => setProgram('all')}>{`All ${cal.data?.entries.length ?? 0}`}</button></li>
              {usedPrograms.map((p) => <li key={p}><button type="button" aria-pressed={program === p} onClick={() => setProgram(p)}>{`${programWord(p)} ${(cal.data?.entries ?? []).filter((e) => e.program === p).length}`}</button></li>)}
            </ul>
          ) : null}
          {cal.isLoading ? <Spinner label="Loading the calendar" />
            : cal.error ? <Alert variant="error"><p>The calendar could not load.</p><Button onClick={() => void cal.refetch()}>Retry</Button></Alert>
              : months.length === 0 ? <EmptyState title="No dates ahead" description="Report dates, notices, renewals and recurring tasks show here." action={<Link className="gp-linkbtn" to={`${prefix}/programs`}>Open programs</Link>} />
                : months.map(([month, list]) => (
                  <div key={month}>
                    <h3 className="gp-month">{month}</h3>
                    <ul className="gp-agenda" aria-label={month}>
                      {withToday(list, cal.data!.generated_at).map((x) => x === 'today'
                        ? <li key="today" className="gp-agenda-today" aria-label="Today"><span>Today</span></li>
                        : <CalendarRow key={x.key} e={x} prefix={prefix} names={names} now={cal.data!.generated_at} />)}
                    </ul>
                  </div>
                ))}
        </section>

        <section className="gp-sec" aria-labelledby="trust-h" data-part="trust-center">
          <h2 id="trust-h" className="gp-sec-h" tabIndex={-1}>Trust center</h2>
          {!isAdmin ? (
            <div className="gp-form"><p>Owners and admins decide who sees certification data.</p>
              <span><AskAdmin slug={slug} compact verb="share a report" what="manages the trust center" subject="GAC Pro: share certification data" /></span></div>
          ) : tc.isLoading ? <Spinner label="Loading the trust center" />
            : tc.error || !tc.data ? <Alert variant="error"><p>The trust center could not load.</p><Button onClick={() => void tc.refetch()}>Retry</Button></Alert>
              : <TrustCenter slug={slug} view={tc.data} names={names} fedrampProfile={fedramp?.profile_id ?? null} onDone={announce} />}
        </section>
      </div>
    </div>
  );
}

/** Insert a "Today" divider before the first entry from today on. */
function withToday(list: CalendarEntry[], nowIso: string): Array<CalendarEntry | 'today'> {
  const i = list.findIndex((e) => e.date >= nowIso);
  const first = list[0]?.date ?? '';
  if (i < 0 || (i === 0 && first.slice(0, 7) !== nowIso.slice(0, 7))) return list;
  return [...list.slice(0, i), 'today', ...list.slice(i)];
}

function NextUp({ slug, prefix, v, programKey, me, names, onDone }: {
  slug: string; prefix: string; v: PackageView; programKey: string; me: string; names: Names; onDone: (m: string) => void;
}) {
  const qc = useQueryClient();
  const focusLater = useFocusAfter();
  const [clearing, setClearing] = useState<PackagePrerequisite | null>(null);
  const state = draftState(v, names);
  const approver = v.lifecycle.approver_user_id ?? v.freeze.approver_user_id;
  const open = v.prerequisites.filter((p) => p.state === 'open');
  const link = (p: PackagePrerequisite) => p.ref_kind === 'scn' && p.ref_id ? `${prefix}/items/scn/${encodeURIComponent(p.ref_id)}?from=program:${programKey}`
    : p.ref_kind === 'acceptance' && p.ref_id ? `${prefix}/exceptions?acceptance=${encodeURIComponent(p.ref_id)}`
      : p.ref_kind === 'cds_grant' && p.ref_id ? `#grant-${p.ref_id}` : null;
  return (
    <section className="gp-next-up" aria-labelledby="next-h" data-part="next-up">
      <h2 id="next-h" className="gp-sec-h">Next up</h2>
      <div className="gp-ow-line">
        <strong>{v.draft.title}</strong>
        <Chip text={state.text} tone={state.tone} />
      </div>
      <p className="gp-dim">{[v.draft.sign_by ? `Sign by ${fmtDate(v.draft.sign_by)}` : null, v.draft.filing_due ? `filing due ${fmtDate(v.draft.filing_due)}` : null,
        approver ? `${nameOf(names, approver)} signs` : null].filter(Boolean).join(' · ')}</p>
      {open.length ? (
        <ol className="gp-plain-list gp-next-list" aria-label="Before it can be signed" id="next-list" tabIndex={-1}>
          {open.map((p) => {
            const to = link(p);
            const mine = !!me && p.owner_user_id === me;
            return (
              <li key={p.prerequisite_id} className="gp-ow-line" id={`next-${p.prerequisite_id}`} tabIndex={-1}>
                <span>{prereqTask(p)}</span>
                <span className="gp-ow-meta">
                  <Chip text={p.blocks_signing ? 'Blocks signing' : 'Won’t block'} tone={p.blocks_signing ? 'red' : 'neutral'} />
                  <span>{[p.owner_user_id ? (mine ? 'You' : nameOf(names, p.owner_user_id)) : 'Unassigned', p.due_at ? `due ${fmtDate(p.due_at)}` : null].filter(Boolean).join(' · ')}</span>
                  {mine && to ? (to.startsWith('#') ? <JumpLink to={to.slice(1)}>Do it</JumpLink> : <Link to={to}>Do it</Link>) : null}
                  {mine && !p.derived ? <Button compact onClick={() => setClearing(p)} aria-label={`Mark done · ${p.title}`}>Mark done…</Button> : null}
                </span>
              </li>
            );
          })}
        </ol>
      ) : null}
      <div className="gp-actions"><Link className="gp-linkbtn" to={`${prefix}/programs/${programKey}/report`}>{`Open in ${programWord(programKey)}`}</Link></div>
      {clearing ? <ClearDialog open withdraw={false} slug={slug} p={clearing} onClose={() => setClearing(null)}
        onDone={async (m) => {
          setClearing(null);
          await Promise.all([qc.invalidateQueries({ queryKey: ['package-drafts', slug] }), qc.invalidateQueries({ queryKey: ['calendar', slug] })]);
          focusLater('next-list'); onDone(m);
        }} /> : null}
    </section>
  );
}

function CalendarRow({ e, prefix, names, now }: { e: CalendarEntry; prefix: string; names: Names; now: string }) {
  const to = !e.ref ? null
    : e.ref.kind === 'package_draft' ? `${prefix}/programs/${e.program ?? SCN_PROGRAM}/report`
      : e.ref.kind === 'acceptance' ? `${prefix}/exceptions?acceptance=${encodeURIComponent(e.ref.id)}`
        : e.ref.kind === 'cds_grant' ? `#grant-${e.ref.id}`
          : `${prefix}/items/${e.ref.kind}/${encodeURIComponent(e.ref.id)}?from=today`;
  const far = Date.parse(e.date) - Date.parse(now) > 14 * DAY;
  return (
    <li className="gp-agenda-row" data-cal={e.kind} data-far={far || undefined}>
      <span className="gp-agenda-date">{new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}</span>
      <span className="gp-ow-main">
        {to ? (to.startsWith('#') ? <JumpLink className="gp-ow-t" to={to.slice(1)}>{e.title}</JumpLink> : <Link className="gp-ow-t" to={to}>{e.title}</Link>) : <span className="gp-ow-t">{e.title}</span>}
        <span className="gp-ow-meta">
          <Chip text={e.status} tone={e.tone === 'red' ? 'red' : e.tone === 'amber' ? 'amber' : e.tone === 'ok' ? 'ok' : 'neutral'} />
          {e.hard ? <span>Hard date</span> : null}
          <span>{programWord(e.program)}</span>
          {e.owner_user_id ? <span>{nameOf(names, e.owner_user_id)}</span> : null}
          {e.provenance && e.provenance.verification !== 'verified' ? <span>Date pending rule verification</span> : null}
        </span>
      </span>
    </li>
  );
}

function TrustCenter({ slug, view, names, fedrampProfile, onDone }: {
  slug: string; view: NonNullable<Awaited<ReturnType<typeof getTrustCenter>>>; names: Names; fedrampProfile: string | null; onDone: (m: string) => void;
}) {
  const qc = useQueryClient();
  const focusLater = useFocusAfter();
  const [approving, setApproving] = useState<CdsAccessRequest | null>(null);
  const [declining, setDeclining] = useState<CdsAccessRequest | null>(null);
  const [extending, setExtending] = useState<TrustCenterGrant | null>(null);
  const [revoking, setRevoking] = useState<TrustCenterGrant | null>(null);
  const [recording, setRecording] = useState(false);
  const [issued, setIssued] = useState<(IssuedAccess & { agency: string }) | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['trust-center', slug] }), qc.invalidateQueries({ queryKey: ['calendar', slug] }), qc.invalidateQueries({ queryKey: ['package-drafts', slug] })]);
  const pending = view.requests.filter((r) => r.state === 'pending');
  const grantRow = (id: string) => `grant-${id}`;
  const usage = async (g: TrustCenterGrant, status: 'confirmed' | 'not_using') => {
    setErr(null);
    try { await confirmGrantUsage(slug, g.grant_id, status); await refresh(); focusLater(grantRow(g.grant_id)); onDone(status === 'confirmed' ? `${g.agency_name} uses the service · confirmed` : `${g.agency_name} · not using`); }
    catch (e) { setErr(errorText(e, 'It could not be recorded.')); }
  };
  const reissue = async (g: TrustCenterGrant) => {
    setErr(null);
    try { const r = await reissueGrantAccess(slug, g.grant_id); setIssued({ ...r.access, agency: g.agency_name }); await refresh(); focusLater('access-issued'); onDone(`New access issued for ${g.agency_name} · the old one stopped working`); }
    catch (e) { setErr(errorText(e, 'Access could not be re-issued.')); }
  };
  return (
    <div className="gp-form">
      <p className="gp-counts">
        <span><b>{view.summary.active_grants}</b> with access</span>
        <span><b className={view.summary.pending_requests ? 'gp-bad' : undefined}>{view.summary.pending_requests}</b> waiting on you</span>
        {view.summary.unconfirmed_usage ? <span><b>{view.summary.unconfirmed_usage}</b> usage to confirm</span> : null}
      </p>
      {err ? <Alert variant="error">{err}</Alert> : null}
      {issued ? <AccessIssued access={issued} onDismiss={() => { setIssued(null); focusLater(grantRow(issued.grant_id)); }} /> : null}

      {pending.length ? (
        <ul className="gp-owed-list" aria-label="Access requests">
          {pending.map((r) => (
            <li key={r.request_id} className="gp-ow" id={`req-${r.request_id}`} tabIndex={-1}>
              <span className="gp-ow-main">
                <span className="gp-ow-t">{r.requester_name}</span>
                <span className="gp-ow-meta"><span>{`${r.requester_kind === 'agency' ? 'Agency' : 'Buyer'} · asked ${fmtDate(r.created_at)} · ${r.nda_signed ? 'NDA signed' : 'no NDA on file'}`}</span></span>
              </span>
              <span className="gp-ow-act">
                <Button compact variant="ghost" onClick={() => setDeclining(r)} aria-label={`Decline · ${r.requester_name}`}>Decline…</Button>
                <Button compact variant="primary" onClick={() => setApproving(r)} aria-label={`Approve · ${r.requester_name}`}>Approve…</Button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <h3 className="gp-sec-h" id="grants-h" tabIndex={-1}>Who can see what</h3>
      {view.grants.length === 0 ? (
        <p className="gp-dim">Nobody outside the workspace has access yet. {fedrampProfile ? <Button compact variant="ghost" onClick={() => setRecording(true)}>Record a request…</Button> : null}</p>
      ) : (
        <ul className="gp-owed-list" aria-labelledby="grants-h">
          {view.grants.map((g) => {
            const active = g.status === 'active';
            return (
              <li key={g.grant_id} className="gp-ow" data-grant={g.grant_id} id={grantRow(g.grant_id)} tabIndex={-1}>
                <span className="gp-ow-main">
                  <span className="gp-ow-line">
                    <span className="gp-ow-t">{g.agency_name}</span>
                    <Chip text={!active ? (g.status === 'revoked' ? 'Revoked' : 'Expired') : g.usage_status === 'confirmed' ? 'Uses the service' : g.usage_status === 'not_using' ? 'Not using' : 'Usage not confirmed'}
                      tone={!active ? 'neutral' : g.usage_status === 'confirmed' ? 'ok' : 'neutral'} />
                  </span>
                  <span className="gp-ow-meta">
                    <span>{active ? `Until ${fmtDate(g.expires_at)}` : g.status === 'revoked' ? `Revoked ${fmtDate(g.revoked_at)}${g.revoke_reason ? ` · ${g.revoke_reason}` : ''}` : `Ended ${fmtDate(g.expires_at)}`}</span>
                    <span>{`Granted by ${firstName(nameOf(names, g.created_by_user_id))} · ${fmtDate(g.created_at)}`}</span>
                    {g.last_access_at ? <span>{`Last opened ${fmtDate(g.last_access_at)}`}</span> : null}
                  </span>
                  <details className="gp-how">
                    <summary>{`${g.package_kinds.length} report type${g.package_kinds.length === 1 ? '' : 's'}${g.queued_kinds.length && active ? ` · ${g.queued_kinds.length} sent when released` : ''}`}</summary>
                    <ul className="gp-plain-list">
                      {g.package_kinds.map((k) => <li key={k}>{`${KIND_LABEL[k] ?? k}${g.queued_kinds.includes(k) && active ? ' · sent when released' : ''}`}</li>)}
                      {g.reads ? <li>{`${g.reads} read${g.reads === 1 ? '' : 's'}`}</li> : null}
                    </ul>
                  </details>
                </span>
                {active ? (
                  <span className="gp-ow-act">
                    {g.usage_status === 'unconfirmed' ? <>
                      <Button compact onClick={() => void usage(g, 'confirmed')} aria-label={`Confirm usage · ${g.agency_name}`}>Confirm usage</Button>
                      <Button compact variant="ghost" onClick={() => void usage(g, 'not_using')} aria-label={`Not using · ${g.agency_name}`}>Not using</Button>
                    </> : null}
                    <Button compact variant="ghost" onClick={() => void reissue(g)} aria-label={`Re-issue access · ${g.agency_name}`}>Re-issue access</Button>
                    <Button compact variant="ghost" onClick={() => setExtending(g)} aria-label={`Extend · ${g.agency_name}`}>Extend…</Button>
                    <Button compact variant="ghost" onClick={() => setRevoking(g)} aria-label={`Revoke · ${g.agency_name}`}>Revoke…</Button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {view.grants.length && fedrampProfile ? <div className="gp-actions"><Button variant="ghost" onClick={() => setRecording(true)}>Record a request…</Button></div> : null}

      <h3 className="gp-sec-h">Access log · 7 days</h3>
      {view.access_log.length === 0 ? <p className="gp-dim gp-small">No access in the last 7 days.</p> : (
        <ul className="gp-plain-list gp-small gp-dim" aria-label="Access log">
          {view.access_log.map((l) => <li key={l.log_id}>{`${fmtDate(l.accessed_at)} · ${l.agency_name} · ${l.action === 'read' ? 'opened a report' : l.action === 'list' ? 'listed reports' : l.action === 'read_denied' ? 'was refused a report' : l.action.replace(/_/g, ' ')}`}</li>)}
        </ul>
      )}

      <ApproveDialog slug={slug} request={approving} releasedKinds={view.released_kinds} onClose={() => setApproving(null)}
        onDone={async (m, access, agency) => { setApproving(null); setIssued({ ...access, agency }); await refresh(); focusLater('access-issued'); onDone(m); }} />
      <DeclineDialog slug={slug} request={declining} onClose={() => setDeclining(null)} onDone={async (m) => { setDeclining(null); await refresh(); focusLater('grants-h'); onDone(m); }} />
      <ExtendDialog slug={slug} grant={extending} onClose={() => setExtending(null)}
        onDone={async (m, id) => { setExtending(null); await refresh(); focusLater(grantRow(id)); onDone(m); }} />
      <RevokeDialog slug={slug} grant={revoking} onClose={() => setRevoking(null)}
        onDone={async (m, id) => { setRevoking(null); await refresh(); focusLater(grantRow(id)); onDone(m); }} />
      {fedrampProfile ? <RecordDialog open={recording} slug={slug} profileId={fedrampProfile} onClose={() => setRecording(false)} onDone={async (m) => { setRecording(false); await refresh(); onDone(m); }} /> : null}
    </div>
  );
}

/** T5-B3: the one-time access panel. The key is never shown again (only its fingerprint is kept). */
function AccessIssued({ access, onDismiss }: { access: IssuedAccess & { agency: string }; onDismiss: () => void }) {
  const [copied, setCopied] = useState('');
  const address = `${typeof window !== 'undefined' ? window.location.origin : 'https://gacpro.io'}/api/cds/v1/packages`;
  const text = `GAC Pro certification data for ${access.agency}\nAddress: ${address}\nAccess key (send as "Authorization: Bearer <key>"): ${access.token}`;
  const copy = async () => {
    setCopied('');
    try { await navigator.clipboard.writeText(text); setCopied('Copied. Send it to them securely.'); }
    catch { setCopied('Copy was unavailable. Select the key and copy it.'); }
  };
  return (
    <div className="gp-state gp-state--ok" id="access-issued" tabIndex={-1} data-part="access-issued" role="region" aria-labelledby="access-issued-h">
      <strong id="access-issued-h">{`Access for ${access.agency} · shown once`}</strong>
      <span>{`Send the access link to ${access.agency_contact_email}. GAC Pro can’t show it again — re-issue if it’s lost.`}</span>
      <Field id="access-key" label="Access key">
        <Input readOnly value={access.token} onFocus={(e) => e.currentTarget.select()} />
      </Field>
      <span className="gp-actions">
        <Button variant="primary" onClick={() => void copy()}>Copy access link</Button>
        <Button variant="ghost" onClick={onDismiss}>Done</Button>
      </span>
      <span role="status" className={copied ? 'gp-dim' : 'gp-sr-only'}>{copied}</span>
    </div>
  );
}

function ApproveDialog({ slug, request, releasedKinds, onClose, onDone }: {
  slug: string; request: CdsAccessRequest | null; releasedKinds: string[]; onClose: () => void; onDone: (m: string, access: IssuedAccess, agency: string) => Promise<void>;
}) {
  const today = dayOf(Date.now());
  const max = dayOf(Date.now() + MAX_GRANT_DAYS * DAY);
  const [until, setUntil] = useState(dayOf(Date.now() + 365 * DAY));
  const [errs, setErrs] = useState<{ until?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const now = request ? request.package_kinds.filter((k) => releasedKinds.includes(k)) : [];
  const later = request ? request.package_kinds.filter((k) => !releasedKinds.includes(k)) : [];
  return (
    <ActionDialog open={!!request} title={request ? `Approve · ${request.requester_name}` : 'Approve'} submitLabel="Approve and issue access" busy={busy} error={err}
      onClose={() => { setErr(null); setErrs({}); onClose(); }}
      onSubmit={async () => {
        if (!request) return false;
        if (!until) { setErrs({ until: 'Choose when access ends.' }); document.getElementById('appr-until')?.focus(); return false; }
        if (until <= today || until > max) { setErrs({ until: `After today and by ${fmtDate(max)}.` }); document.getElementById('appr-until')?.focus(); return false; }
        setBusy(true); setErr(null);
        try {
          const r = await approveAccessRequest(slug, request.request_id, { expires_at: `${until}T23:59:59.000Z` });
          await onDone(`${request.requester_name} can see it until ${fmtDate(until)} · copy their access now`, r.access, request.requester_name);
          return true;
        } catch (e) { setErr(errorText(e, 'Access could not be granted.')); return false; }
        finally { setBusy(false); }
      }}>
      {request ? <p className="gp-dim">{`${request.nda_signed ? 'NDA signed' : 'No NDA on file'} · ${request.requester_email}`}</p> : null}
      <p className="gp-sec-h">They’ll receive</p>
      <ul className="gp-plain-list">
        {now.map((k) => <li key={k}>{`${KIND_LABEL[k] ?? k} · now`}</li>)}
        {later.map((k) => <li key={k}>{`${KIND_LABEL[k] ?? k} · when released`}</li>)}
      </ul>
      <Field id="appr-until" label="Access until" required hint={`Up to ${MAX_GRANT_DAYS} days`} error={errs.until}>
        <Input type="date" min={today} max={max} value={until} onChange={(e) => { setUntil(e.currentTarget.value); setErrs({}); }} />
      </Field>
      <p className="gp-dim">Their access link is shown once, right after you approve.</p>
    </ActionDialog>
  );
}

function ExtendDialog({ slug, grant, onClose, onDone }: { slug: string; grant: TrustCenterGrant | null; onClose: () => void; onDone: (m: string, grantId: string) => Promise<void> }) {
  const max = dayOf(Date.now() + MAX_GRANT_DAYS * DAY);
  const [until, setUntil] = useState('');
  const [why, setWhy] = useState('');
  const [errs, setErrs] = useState<{ until?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const min = grant ? dayOf(Date.parse(grant.expires_at) + DAY) : '';
  useEffect(() => { if (grant) setUntil(max); }, [grant?.grant_id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <ActionDialog open={!!grant} title={grant ? `Extend · ${grant.agency_name}` : 'Extend'} submitLabel="Extend access" busy={busy} error={err}
      onClose={() => { setErr(null); setErrs({}); setWhy(''); onClose(); }}
      onSubmit={async () => {
        if (!grant) return false;
        if (!until || until < min || until > max) { setErrs({ until: `After ${fmtDate(grant.expires_at)} and by ${fmtDate(max)}.` }); document.getElementById('ext-until')?.focus(); return false; }
        setBusy(true); setErr(null);
        try { await extendGrant(slug, grant.grant_id, { expires_at: `${until}T23:59:59.000Z`, ...(why.trim() ? { reason: why.trim() } : {}) }); setWhy(''); await onDone(`${grant.agency_name} · access until ${fmtDate(until)}`, grant.grant_id); return true; }
        catch (x) { setErr(errorText(x, 'Access could not be extended.')); return false; }
        finally { setBusy(false); }
      }}>
      {grant ? <p className="gp-dim">{`Now until ${fmtDate(grant.expires_at)}`}</p> : null}
      <Field id="ext-until" label="New end date" required hint={`Up to ${MAX_GRANT_DAYS} days from today`} error={errs.until}>
        <Input type="date" min={min} max={max} value={until} onChange={(e) => { setUntil(e.currentTarget.value); setErrs({}); }} />
      </Field>
      <Field id="ext-why" label="Why" hint="Optional">
        <Textarea rows={2} maxLength={2000} value={why} onChange={(e) => setWhy(e.currentTarget.value)} />
      </Field>
    </ActionDialog>
  );
}

function RevokeDialog({ slug, grant, onClose, onDone }: { slug: string; grant: TrustCenterGrant | null; onClose: () => void; onDone: (m: string, grantId: string) => Promise<void> }) {
  const [why, setWhy] = useState('');
  const [errs, setErrs] = useState<{ why?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <ActionDialog open={!!grant} title={grant ? `Revoke · ${grant.agency_name}` : 'Revoke'} submitLabel="Revoke access" danger busy={busy} error={err}
      onClose={() => { setErr(null); setErrs({}); setWhy(''); onClose(); }}
      onSubmit={async () => {
        if (!grant) return false;
        if (why.trim().length < 3) { setErrs({ why: 'Say why access ends (kept with the grant).' }); document.getElementById('rev-why')?.focus(); return false; }
        setBusy(true); setErr(null);
        try { await revokeGrant(slug, grant.grant_id, why.trim()); setWhy(''); await onDone(`${grant.agency_name} · access revoked`, grant.grant_id); return true; }
        catch (x) { setErr(errorText(x, 'Access could not be revoked.')); return false; }
        finally { setBusy(false); }
      }}>
      <p>{grant ? `${grant.agency_name} loses access now; their access link stops working.` : ''}</p>
      <Field id="rev-why" label="Reason" required error={errs.why}>
        <Textarea rows={3} maxLength={2000} value={why} onChange={(e) => { setWhy(e.currentTarget.value); setErrs({}); }} />
      </Field>
    </ActionDialog>
  );
}

function DeclineDialog({ slug, request, onClose, onDone }: { slug: string; request: CdsAccessRequest | null; onClose: () => void; onDone: (m: string) => Promise<void> }) {
  const [why, setWhy] = useState('');
  const [errs, setErrs] = useState<{ why?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <ActionDialog open={!!request} title={request ? `Decline · ${request.requester_name}` : 'Decline'} submitLabel="Decline" busy={busy} error={err} danger
      onClose={() => { setErr(null); setErrs({}); onClose(); }}
      onSubmit={async () => {
        if (!request) return false;
        if (!why.trim()) { setErrs({ why: 'Say why (kept with the request).' }); document.getElementById('decl-why')?.focus(); return false; }
        setBusy(true); setErr(null);
        try { await declineAccessRequest(slug, request.request_id, why.trim()); setWhy(''); await onDone(`Declined · ${request.requester_name}`); return true; }
        catch (e) { setErr(errorText(e, 'It could not be declined.')); return false; }
        finally { setBusy(false); }
      }}>
      <Field id="decl-why" label="Reason" required error={errs.why}>
        <Textarea rows={3} maxLength={2000} value={why} onChange={(e) => { setWhy(e.currentTarget.value); setErrs({}); }} />
      </Field>
    </ActionDialog>
  );
}

function RecordDialog({ open, slug, profileId, onClose, onDone }: { open: boolean; slug: string; profileId: string; onClose: () => void; onDone: (m: string) => Promise<void> }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<'agency' | 'buyer'>('agency');
  const [nda, setNda] = useState('no');
  const [errs, setErrs] = useState<{ name?: string; email?: string }>({});
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <ActionDialog open={open} title="Record an access request" submitLabel="Record request" busy={busy} error={err}
      onClose={() => { setErr(null); setErrs({}); onClose(); }}
      onSubmit={async () => {
        const e: typeof errs = {};
        if (name.trim().length < 2) e.name = 'Who is asking?';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Their work email, like isso@agency.gov.';
        setErrs(e);
        if (e.name || e.email) { document.getElementById(e.name ? 'rq-name' : 'rq-email')?.focus(); return false; }
        setBusy(true); setErr(null);
        try { await recordAccessRequest(slug, { profile_id: profileId, requester_name: name.trim(), requester_email: email.trim(), requester_kind: kind, nda_signed: nda === 'yes' }); setName(''); setEmail(''); await onDone('Request recorded · waiting on your decision'); return true; }
        catch (x) { setErr(errorText(x, 'It could not be recorded.')); return false; }
        finally { setBusy(false); }
      }}>
      <Field id="rq-name" label="Who is asking" required error={errs.name}><Input value={name} maxLength={200} onChange={(e) => { setName(e.currentTarget.value); setErrs((x) => ({ ...x, name: undefined })); }} /></Field>
      <Field id="rq-email" label="Email" required error={errs.email}><Input type="email" value={email} maxLength={254} onChange={(e) => { setEmail(e.currentTarget.value); setErrs((x) => ({ ...x, email: undefined })); }} /></Field>
      <div className="gp-form-row">
        <Field id="rq-kind" label="They are"><Select value={kind} onChange={(e) => setKind(e.currentTarget.value as 'agency' | 'buyer')}><option value="agency">An agency</option><option value="buyer">A buyer</option></Select></Field>
        <Field id="rq-nda" label="NDA signed?"><Select value={nda} onChange={(e) => setNda(e.currentTarget.value)}><option value="no">No</option><option value="yes">Yes</option></Select></Field>
      </div>
    </ActionDialog>
  );
}
